import { cn } from "~/ui-rtk/utils/tailwind-utils"

export type TSelectOption = {
  label: string
  value: string | number
  disabled?: boolean
}
export type TSelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  options: TSelectOption[]
  placeholder?: string
  isError?: boolean
  classes?: Partial<{
    select: string
  }>
}

export default function Select({
  className,
  options,
  placeholder,
  isError = false,
  classes,
  ...props
}: TSelectProps) {
  // `py-0` is load-bearing, not cosmetic. @tailwindcss/forms puts
  // `padding-top/bottom: 0.5rem` on every bare `select` in the *base* layer, and
  // DaisyUI's `.select` only resets `padding-inline`. Those 16px survive every
  // height override, so any `h-*` below ~48px eats straight into the content box
  // and clips the selected label -- at h-[30px] you see its top half, at
  // h-[24px] you see nothing at all. Zeroing it is vertically symmetric, so
  // default-height selects render pixel-for-pixel the same. Callers that size a
  // select by its own padding (`h-auto py-1`) still win via tw-merge.
  const cx = cn(
    "select w-full bg-basic-dark-blue border border-basic-blue py-0 disabled:opacity-50",
    isError && "border-states-error",
    className,
  )
  // The placeholder is the empty value, so a controlled `value=""` shows it.
  // Without a match React marks the first enabled option selected, and the
  // user sees a choice that looks picked while the state is still empty.
  // Uncontrolled selects start on it through `defaultValue`.
  const placeholderOption = placeholder ? (
    <option value="" disabled>
      {placeholder}
    </option>
  ) : null
  const placeholderDefault =
    placeholder && props.value === undefined && props.defaultValue === undefined
      ? { defaultValue: "" }
      : {}
  return (
    <select
      className={cn(cx, classes?.select)}
      {...placeholderDefault}
      {...props}
    >
      {placeholderOption}
      {options.map(option => (
        <option
          key={option.value}
          disabled={option.disabled}
          value={option.value}
        >
          {option.label}
        </option>
      ))}
    </select>
  )
}
